import React, { useEffect, useRef } from 'react';
import { 
  ChevronUp,
  ChevronDown,
  CornerDownLeft,
} from 'lucide-react';
import { ExecutionDocument } from '../../../../../shared/services/documentService';
import { cn } from "@/lib/utils";
import { getPillProps } from '../../../utils/documentStyles';
import { ChatPillData } from '../../../types/chatTypes';

export interface MentionSuggestion extends ChatPillData {
  document?: ExecutionDocument;
  folderId?: string;
  icon?: React.ReactNode;
  /** Catalog template type (word, excel, …) for execution_template rows */
  templateType?: string | null;
}

interface MentionAutocompleteProps {
  visible: boolean;
  query: string;
  suggestions: MentionSuggestion[];
  selectedIndex: number;
  onSelect: (suggestion: MentionSuggestion) => void;
  onClose: () => void;
  isLoading?: boolean;
}

const FileTypeIcon = ({
  name,
  type,
  templateType,
  className,
}: {
  name: string;
  type: string;
  templateType?: string | null;
  className?: string;
}) => {
  const isEt = type === 'execution_template';
  const ext =
    isEt && templateType === 'excel' ? '.xlsx' : isEt ? '.docx' : '';
  const { iconPath } = getPillProps({
    name: isEt ? `${name}${ext}` : name,
    type: isEt ? 'template' : type,
  });
  return <img src={iconPath} className={className} alt="" />;
};

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  visible,
  suggestions,
  selectedIndex,
  onSelect,
  onClose,
  isLoading = false
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && listRef.current && suggestions.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedIndex, visible, suggestions]);

  if (!visible) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-[9998] cursor-default"
        onClick={onClose}
      />
      
      <div 
        className="absolute bottom-full left-3 mb-2 z-[9999] w-[min(420px,calc(100vw-24px))] max-w-[calc(100%-24px)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-2 fade-in flex flex-col"
      >
        <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50 flex justify-between items-center border-b border-slate-100">
          <span>References</span>
          {isLoading && <span className="animate-pulse normal-case font-normal">Loading...</span>}
        </div>

        <div 
          ref={listRef}
          className="max-h-[240px] overflow-y-auto"
        >
          {suggestions.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">
              No results found
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(suggestion);
                }}
                onClick={() => onSelect(suggestion)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-all text-left outline-none",
                  index === selectedIndex 
                    ? "bg-primary/10 text-primary shadow-sm border-l-2 border-primary" 
                    : "text-slate-700 hover:bg-slate-50 border-l-2 border-transparent"
                )}
              >
                <div className={cn(
                  "flex items-center justify-center shrink-0",
                  index === selectedIndex ? "text-primary" : "text-slate-400"
                )}>
                  {suggestion.icon || (
                    <FileTypeIcon
                      name={suggestion.name}
                      type={suggestion.type}
                      templateType={suggestion.templateType}
                      className="h-3.5 w-3.5"
                    />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "truncate",
                    index === selectedIndex ? "font-semibold" : "font-medium"
                  )}>
                    {suggestion.name}
                  </div>
                  
                  {(suggestion.knowledgeBaseName || suggestion.type === 'execution_template') && (
                    <div className={cn(
                      "text-[9px] truncate opacity-70",
                      index === selectedIndex ? "text-primary" : "text-slate-400"
                    )}>
                      {suggestion.type === 'execution_template'
                        ? 'Execution template'
                        : suggestion.knowledgeBaseName}
                    </div>
                  )}
                </div>

                {index === selectedIndex && (
                  <CornerDownLeft className="h-2.5 w-2.5 text-primary/50 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Pie de página con atajos */}
        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-[9px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0 rounded border bg-white shadow-sm font-sans text-slate-500 flex items-center">
                <ChevronUp className="h-2 w-2" />
                <ChevronDown className="h-2 w-2" />
              </kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0 rounded border bg-white shadow-sm font-sans text-slate-500">
                Enter
              </kbd>
              Select
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
